// Sorcery TCG precon decks the player can earn as they progress.
//
// These are the "Precon 3.0" lists: Constructed-legal 60/30 rebuilds of the
// official precons authored by QA (MisprintedChris, August 2026). The card
// lists are the source of truth for new claims; the server carries the same
// lists in valkenhall-server/src/data/precon-decks-v3.json for the one-shot
// migration that updated existing owners' decks and collections. Change one
// and regenerate the other. `scripts/fetch-starter-decks.js` regenerates the
// OLD Curiosa precons and will clobber these lists. Do not re-run it.
//
// All eight decks are pickable from the very first L1 starter choice;
// each milestone after that (L2/L3/L5/L10/L15/L20/L25. Mirrored from
// DECK_MILESTONE_LEVELS in valkenhall-server/src/utils/progression.js)
// lets the player pick another deck, with already-owned decks greyed
// out. Seven milestones plus the L1 pick equals the full 8-deck pool
// by L25. STARTER_DECKS and GOTHIC_DECKS stay grouped here purely as
// a data-source organisation (and so scripts/fetch-starter-decks.js
// still has a stable target); the picker UI uses PROGRESSION_DECKS.
// the combined pool of 8.
//
//   flamecaller:  clczbmbxb0085v5ug7eyxn2dk  (Fire)
//   geomancer:    clczbmbxb008hv5uggpws66fe  (Earth)
//   waveshaper:   clczbmbxb008ev5ugiovmxcw1  (Water)
//   sparkmage:    clczbmbxb008av5ugml35rdwt  (Air)
//   savior:       cmip2v28500k6l204a5bxo5oh  (Earth/Water)
//   necromancer:  cmip2vh9y00kal204778yzvbd  (Air/Fire)
//   persecutor:   cmip2vwc100khl2043cgtpae7  (Earth/Fire)
//   harbinger:    cmip2w8qa002mjl04intv339z  (Air/Water)

import { ART_GROUP_BASE, printingsInArtGroup } from '../sorcery/artGroups';

export const STARTER_DECKS = [
  {
    id: 'flamecaller',
    name: 'Flamecaller',
    pool: 'beta',
    elements: ['Fire'],
    description: 'Unleash an army of Demons and fire Minions while turning the life loss itself into a weapon. Control the battlefield with explosive Magic, then build toward powerful demonic threats as the flames spread. Flamecaller combines destructive spells, aggressive Minions, and overwhelming firepower to burn through anything standing in your way.',
    strategy: 'Demon Pact',
    keyCards: ['Ignis Rex', 'Day of Judgment', 'Greater Blood Demon'],
    tutorial: 'Feed fire minions into the cemetery, then tap the Flamecaller to banish them all and unleash a single projectile, dealing damage equal to their combined fire affinity. The more fire you stack in the dead pile, the bigger the blast.',
    curiosaDeckId: 'clczbmbxb0085v5ug7eyxn2dk',
    avatar: 'Flamecaller',
    spellbook: [
      { name: 'Angry Mob', qty: 2 },
      { name: 'Blaze', qty: 2 },
      { name: 'Blaze of Glory', qty: 1 },
      { name: 'Cranky Overseer', qty: 1 },
      { name: 'Day of Judgment', qty: 1 },
      { name: 'Disintegrate', qty: 1 },
      { name: 'Driftwood Marrows', qty: 2 },
      { name: 'Enraged Familiar', qty: 4 },
      { name: 'Fallen Angel', qty: 3 },
      { name: 'Firebolts', qty: 2 },
      { name: 'Flayer', qty: 3 },
      { name: 'Ghostfire', qty: 1 },
      { name: 'Gift of the Serpent', qty: 2 },
      { name: 'Greater Blood Demon', qty: 2 },
      { name: 'Ignis Rex', qty: 1 },
      { name: 'Infernal Legion', qty: 1 },
      { name: 'Lesser Blood Demon', qty: 4 },
      { name: 'Lord of Destruction', qty: 1 },
      { name: 'Major Explosion', qty: 1 },
      { name: 'Màzuj Ifrit', qty: 1 },
      { name: 'Minor Explosion', qty: 2 },
      { name: 'Pit Vipers', qty: 3 },
      { name: 'Poison Nova', qty: 1 },
      { name: 'Redmane Hyena', qty: 3 },
      { name: 'Searing Truth', qty: 2 },
      { name: 'Shackled Demon', qty: 2 },
      { name: 'Shatter Strike', qty: 2 },
      { name: 'Slobbermaw', qty: 3 },
      { name: 'Torshammar Trinket', qty: 1 },
      { name: 'Vandal Imp', qty: 1 },
      { name: 'Vile Imp', qty: 4 },
    ],
    atlas: [
      { name: 'Accursed Desert', qty: 2 },
      { name: 'Arid Desert', qty: 4 },
      { name: 'Bonfire', qty: 2 },
      { name: 'Funeral Pyre', qty: 1 },
      { name: 'Leadworks', qty: 2 },
      { name: 'Molten Maar', qty: 3 },
      { name: 'Red Desert', qty: 4 },
      { name: 'Remote Desert', qty: 4 },
      { name: 'Shifting Sands', qty: 3 },
      { name: 'Vast Desert', qty: 4 },
      { name: 'Vesuvius', qty: 1 },
    ],
    collection: [
      { name: 'Hellhounds', qty: 3 },
    ],
  },
  {
    id: 'geomancer',
    name: 'Geomancer',
    pool: 'beta',
    elements: ['Earth'],
    description: 'Build a thriving realm and lead its people into battle. Establish villages and strongholds, gather a growing army of Mortals, and use the power of the Earth to reshape the battlefield. Geomancer rewards careful development, turning your expanding realm into an unstoppable force.',
    strategy: 'Mortal Kingdom',
    keyCards: ['King of the Realm', 'Lady Iseult', 'Crown Prince'],
    tutorial: 'Playing earth sites fills the voids adjacent to your Geomancer with Rubble. Tap the Geomancer to swap that Rubble for the topmost site of your atlas, manufacturing terrain on demand. Flood the field with cheap warriors, then finish with oversized titans and siege weapons.',
    curiosaDeckId: 'clczbmbxb008hv5uggpws66fe',
    avatar: 'Geomancer',
    spellbook: [
      { name: 'Albespine Pikemen', qty: 3 },
      { name: 'Amazon Warriors', qty: 3 },
      { name: 'Arjaro Exorcist', qty: 1 },
      { name: 'Belfry', qty: 1 },
      { name: 'Belmotte Longbowmen', qty: 4 },
      { name: 'Border Militia', qty: 4 },
      { name: 'Bury', qty: 2 },
      { name: 'Call to War', qty: 3 },
      { name: 'Cave-In', qty: 2 },
      { name: 'Crown Prince', qty: 1 },
      { name: 'Dalcean Phalanx', qty: 2 },
      { name: 'Earthquake', qty: 1 },
      { name: 'Eltham Townsfolk', qty: 3 },
      { name: 'Gift of the Wolf', qty: 4 },
      { name: 'Guards!', qty: 2 },
      { name: 'Holy Warrior', qty: 4 },
      { name: 'Homecoming', qty: 1 },
      { name: 'House Arn Bannerman', qty: 3 },
      { name: 'King of the Realm', qty: 1 },
      { name: 'Lady Iseult', qty: 1 },
      { name: 'Landmass', qty: 2 },
      { name: 'Mayor of Milborne', qty: 2 },
      { name: 'Miracle Workers', qty: 2 },
      { name: 'Order of the White Wing', qty: 3 },
      { name: 'Paladins of Bazia', qty: 1 },
      { name: 'Sawbones', qty: 1 },
      { name: 'Torshammar Trinket', qty: 1 },
      { name: 'Tragedy Worrywart', qty: 2 },
    ],
    atlas: [
      { name: 'Bedrock', qty: 3 },
      { name: 'Common Village', qty: 4 },
      { name: 'Forlorn Keep', qty: 3 },
      { name: 'Hillside Chapel', qty: 3 },
      { name: 'Holy Ground', qty: 2 },
      { name: 'Humble Village', qty: 4 },
      { name: 'Pillar of Zeiros', qty: 1 },
      { name: 'Rustic Village', qty: 4 },
      { name: 'Simple Village', qty: 4 },
      { name: 'Vantage Hills', qty: 2 },
    ],
    collection: [
      { name: 'Penitent Knight', qty: 3 },
    ],
  },
  {
    id: 'waveshaper',
    name: 'Waveshaper',
    pool: 'beta',
    elements: ['Water'],
    description: 'Harness the power of Water to control the battlefield and overwhelm your opponent. Flood the Realm with rivers and waterways, disrupt enemy plans, and unleash powerful sea Minions from the depths. As the tides rise, your Minions and Magic work together to sweep aside resistance and Submerge your opponent beneath an unstoppable wave.',
    strategy: 'Drown the Realm',
    keyCards: ['Great Old One', 'The Flood', 'Maelström'],
    tutorial: 'Tap the Waveshaper to flood a site next to your body of water. Any enemy minions there without Submerge stay tapped and miss a turn, locking the board while you build toward massive late-game threats like the Diluvian Kraken.',
    curiosaDeckId: 'clczbmbxb008ev5ugiovmxcw1',
    avatar: 'Waveshaper',
    spellbook: [
      { name: 'Assimilator Snail', qty: 1 },
      { name: 'Call of the Sea', qty: 1 },
      { name: 'Coral-Reef Kelpie', qty: 3 },
      { name: 'Drown', qty: 2 },
      { name: 'Drowned', qty: 4 },
      { name: 'Failed Mutation', qty: 2 },
      { name: 'Finwife', qty: 3 },
      { name: 'Great Old One', qty: 1 },
      { name: 'Guile Sirens', qty: 3 },
      { name: 'Kiss of Judas', qty: 1 },
      { name: 'Meat Hook', qty: 2 },
      { name: 'Mesmer Demon', qty: 4 },
      { name: 'Mester Stoor Worm', qty: 1 },
      { name: 'Order of the Sacred Oak', qty: 2 },
      { name: 'Overflow', qty: 4 },
      { name: 'Polar Explorers', qty: 1 },
      { name: 'Pollimorph', qty: 1 },
      { name: 'Porcupine Pufferfish', qty: 3 },
      { name: 'Riptide', qty: 3 },
      { name: 'Sacred Stag', qty: 1 },
      { name: 'Seirawan Hydra', qty: 1 },
      { name: 'Selfsame Simulacrum', qty: 1 },
      { name: 'Stormy Seas', qty: 2 },
      { name: 'Swap', qty: 2 },
      { name: 'The Flood', qty: 1 },
      { name: 'Tide Naiads', qty: 3 },
      { name: 'Torshammar Trinket', qty: 1 },
      { name: 'Tufted Turtles', qty: 3 },
      { name: 'Winter Nymph', qty: 2 },
      { name: 'Wrath of the Sea', qty: 1 },
    ],
    atlas: [
      { name: 'Autumn River', qty: 4 },
      { name: 'Croaking Swamp', qty: 4 },
      { name: 'Island Leviathan', qty: 1 },
      { name: 'Maelström', qty: 1 },
      { name: 'Mudslide', qty: 3 },
      { name: 'Peculiar Port', qty: 3 },
      { name: 'Spring River', qty: 4 },
      { name: 'Summer River', qty: 4 },
      { name: 'Thin Ice', qty: 2 },
      { name: 'Troll Bridge', qty: 2 },
      { name: 'Undertow', qty: 2 },
    ],
    collection: [
      { name: 'Horrible Hybrids', qty: 3 },
    ],
  },
  {
    id: 'sparkmage',
    name: 'Sparkmage',
    pool: 'beta',
    elements: ['Air'],
    description: 'Take to the Air with a fast, unpredictable army of Airborne Minions and powerful Magic. Blink and reposition your forces, then strike from unexpected angles while lightning and other Magic disrupts your opponent\'s plans. Sparkmage rewards clever movement, surprise attacks, and explosive bursts of magic to keep your enemy constantly off balance.',
    strategy: 'Airborne Assault',
    keyCards: ['Witherwing Hero', 'The Hexham Haunts', 'Zephyranne Airship'],
    tutorial: 'Every air spell you cast in a turn charges the Sparkmage. Tap to snipe a random unit at a nearby location for damage equal to the air affinity you spent that turn. Stack spells before pulling the trigger, and lean on the Lucky Charm to take the randomness out of your dice-roll cards.',
    curiosaDeckId: 'clczbmbxb008av5ugml35rdwt',
    avatar: 'Sparkmage',
    spellbook: [
      { name: 'Avenging Angel', qty: 3 },
      { name: 'Blink', qty: 3 },
      { name: 'Cast into Exile', qty: 3 },
      { name: 'Chain Lightning', qty: 2 },
      { name: 'Cloud Spirit', qty: 4 },
      { name: 'Displace', qty: 2 },
      { name: 'East-West Dragon', qty: 1 },
      { name: 'Grandmaster Wizard', qty: 1 },
      { name: 'Gyre Hippogriffs', qty: 3 },
      { name: 'Haast Eagle', qty: 3 },
      { name: 'Highland Falconer', qty: 1 },
      { name: 'Hounds of Ondaros', qty: 1 },
      { name: 'Legion of Gall', qty: 1 },
      { name: 'Lightning Bolt', qty: 2 },
      { name: 'Magic Missiles', qty: 2 },
      { name: 'Nimbus Jinn', qty: 1 },
      { name: 'Ophanim', qty: 1 },
      { name: 'Pith Imp', qty: 3 },
      { name: 'Plague Rat', qty: 3 },
      { name: 'Plumed Pegasus', qty: 4 },
      { name: 'Sturmgeist', qty: 1 },
      { name: 'The Doom of Dilmun', qty: 1 },
      { name: 'The Hexham Haunts', qty: 1 },
      { name: 'Thunderstorm', qty: 2 },
      { name: 'Torshammar Trinket', qty: 1 },
      { name: 'Vampire Bats', qty: 4 },
      { name: 'Vesper Swarm', qty: 2 },
      { name: 'Whirling Blades', qty: 2 },
      { name: 'Witherwing Hero', qty: 1 },
      { name: 'Zephyranne Airship', qty: 1 },
    ],
    atlas: [
      { name: 'Accursed Tower', qty: 2 },
      { name: 'Beacon', qty: 2 },
      { name: 'Cloud City', qty: 1 },
      { name: 'Dark Alley', qty: 2 },
      { name: 'Dark Tower', qty: 2 },
      { name: 'Fields of Phyxis', qty: 2 },
      { name: 'Gothic Tower', qty: 2 },
      { name: 'Lone Tower', qty: 2 },
      { name: 'Mountain Pass', qty: 3 },
      { name: 'Perilous Bridge', qty: 1 },
      { name: 'Spore Spouts', qty: 3 },
      { name: 'Treetop Hideout', qty: 4 },
      { name: 'Updraft Ridge', qty: 3 },
      { name: 'Watchtower', qty: 1 },
    ],
  },
];

export const GOTHIC_DECKS = [
  {
    id: 'savior',
    name: 'Savior',
    pool: 'gothic',
    elements: ['Earth', 'Water'],
    description: 'Lead a powerful force of warriors, cavalry, and Giants into battle. Protect your Charge Minions with Ward and other tricks just before they strike, catching your opponent off guard and turning combat in your favor. Build your army early, then reinforce it with a powerful Giant endgame to bring even more threats to the battlefield.',
    strategy: 'Charging Giants',
    keyCards: ['Rebecks', 'Blunderbore', 'Mountain Giant'],
    tutorial: 'Pay one mana to grant Ward to any minion you summoned this turn, shielding it from a targeted spell or ability. Save the trigger for your biggest threats or the fragile payoff pieces you cannot afford to lose, then sweep the board with Holy Nova.',
    curiosaDeckId: 'cmip2v28500k6l204a5bxo5oh',
    avatar: 'Savior',
    avatarArtGroup: 'got-savior-pd',
    spellbook: [
      { name: 'Angel Ascendant', qty: 2 },
      { name: 'Atlas Wanderers', qty: 2 },
      { name: 'Bless', qty: 2 },
      { name: 'Blunderbore', qty: 1 },
      { name: 'Bury', qty: 2 },
      { name: 'Cave-In', qty: 1 },
      { name: 'Croll Morlocks', qty: 3 },
      { name: 'Faith Incarnate', qty: 2 },
      { name: 'Firebolts', qty: 2 },
      { name: 'Gift of the Serpent', qty: 2 },
      { name: 'Harpyon Urge', qty: 2 },
      { name: 'Holy Nova', qty: 2 },
      { name: 'Landmass', qty: 2 },
      { name: 'Lash', qty: 2 },
      { name: 'Monstrous Lion', qty: 2 },
      { name: 'Mountain Giant', qty: 1 },
      { name: 'Onslaught', qty: 2 },
      { name: 'Petrosian Cavalry', qty: 4 },
      { name: 'Rebecks', qty: 1 },
      { name: 'Redcap Powries', qty: 3 },
      { name: 'Rhitta Gawr of Snowdonia', qty: 1 },
      { name: 'Saracen Raiders', qty: 3 },
      { name: 'Shatter Strike', qty: 1 },
      { name: 'Siege Giant', qty: 3 },
      { name: 'Slumbering Giantess', qty: 3 },
      { name: 'Torshammar Trinket', qty: 1 },
      { name: 'War Horse', qty: 3 },
      { name: 'Wild Fanatic', qty: 4 },
      { name: 'Wraetannis Titan', qty: 1 },
    ],
    atlas: [
      { name: 'Active Volcano', qty: 1 },
      { name: 'Arid Desert', qty: 2 },
      { name: 'Autumn Bloom', qty: 2 },
      { name: 'Blessed Village', qty: 3 },
      { name: 'Common Village', qty: 2 },
      { name: 'Desert Bloom', qty: 2 },
      { name: 'Fertile Earth', qty: 1 },
      { name: 'Humble Village', qty: 2 },
      { name: 'Poisoned Well', qty: 3 },
      { name: 'Red Desert', qty: 2 },
      { name: 'Remote Desert', qty: 2 },
      { name: 'Rustic Village', qty: 2 },
      { name: 'Simple Village', qty: 2 },
      { name: 'Smokestacks of Gnaak', qty: 1 },
      { name: 'Steppe', qty: 1 },
      { name: 'Vast Desert', qty: 2 },
    ],
  },
  {
    id: 'necromancer',
    name: 'Necromancer',
    pool: 'gothic',
    elements: ['Air', 'Fire'],
    description: 'Command an army of Undead Minions and turn the Cemetery into a weapon. Build your Undead forces, bury your enemies beneath an endless tide of skeletons and zombies, then bring your fallen Minions back to fight again. The longer the battle goes, the more dangerous your army becomes.',
    strategy: 'Undead Horde',
    keyCards: ['Army of the Dead', 'Young Master Damion', 'Black Mass'],
    tutorial: 'Pay one mana each turn to summon a Skeleton token at the Necromancer\'s site. Use them as sacrifice fodder for graveyard payoffs like Bone Spear, or simply keep marching them forward. Death is your resource, and inevitability is the win condition.',
    curiosaDeckId: 'cmip2vh9y00kal204778yzvbd',
    avatar: 'Necromancer',
    avatarArtGroup: 'got-necromancer-pd',
    spellbook: [
      { name: 'Arcade of Bones', qty: 1 },
      { name: 'Army of the Dead', qty: 1 },
      { name: 'Bitter Departed', qty: 3 },
      { name: 'Black Mass', qty: 1 },
      { name: 'Bone Spear', qty: 2 },
      { name: 'Buried Alive', qty: 3 },
      { name: 'Carrionette', qty: 3 },
      { name: 'Clairvoyant', qty: 1 },
      { name: 'Crawling Congregation', qty: 1 },
      { name: 'Daperyll Vampire', qty: 2 },
      { name: 'Death Knight', qty: 3 },
      { name: 'Entombed', qty: 4 },
      { name: 'Kiss of Death', qty: 3 },
      { name: 'Master Necromancer', qty: 1 },
      { name: 'Noisome Twosome', qty: 4 },
      { name: 'Pile of Skulls', qty: 1 },
      { name: 'Raise Dead', qty: 2 },
      { name: 'Raise Militia', qty: 4 },
      { name: 'Renatus Trueblood', qty: 1 },
      { name: 'Screamer', qty: 4 },
      { name: 'Skeleton Mage', qty: 3 },
      { name: 'Sphere of Animosity', qty: 1 },
      { name: 'Spire Lich', qty: 3 },
      { name: 'Stygian Archers', qty: 3 },
      { name: 'Torshammar Trinket', qty: 1 },
      { name: 'Witching Hour', qty: 3 },
      { name: 'Young Master Damion', qty: 1 },
    ],
    atlas: [
      { name: 'Autumn Bloom', qty: 2 },
      { name: 'Boneyard', qty: 1 },
      { name: 'Boulevard of Bones', qty: 1 },
      { name: 'Crossroads', qty: 1 },
      { name: 'Dark Tower', qty: 2 },
      { name: 'Den of Evil', qty: 1 },
      { name: 'Fertile Earth', qty: 1 },
      { name: 'Forgotten Tomb', qty: 1 },
      { name: 'Forsaken Crypt', qty: 3 },
      { name: 'Gothic Tower', qty: 2 },
      { name: 'Impenetrable Copse', qty: 2 },
      { name: 'Lone Tower', qty: 2 },
      { name: 'Mountain Peaks', qty: 1 },
      { name: 'Open Grave', qty: 4 },
      { name: 'Shallow Grave', qty: 2 },
      { name: 'Sold-out Cemetery', qty: 1 },
      { name: 'Twilight Bloom', qty: 2 },
      { name: 'Windmill', qty: 1 },
    ],
    collection: [
      { name: 'Ghoul', qty: 3 },
    ],
  },
  {
    id: 'persecutor',
    name: 'Persecutor',
    pool: 'gothic',
    elements: ['Earth', 'Fire'],
    description: 'Punish your opponent from every angle with relentless Minions, Weapons, and disruptive Magic. Build a dangerous board of Demons, Monsters, and other Evil threats while using tricks and attacks to keep your opponent off balance. This deck thrives on pressure, forcing difficult choices until your opponent has nowhere left to hide.',
    strategy: 'Wicked Arms',
    keyCards: ['Grigori Rasputin', 'Sir Mordred', 'Blade of Thorns'],
    tutorial: 'Each turn the Persecutor may take one step toward the closest Evil minion to close the distance, or instead brand any enemy so your cards treat them as Evil for the turn. Mark whichever target your anti-corruption arsenal is ready to punish.',
    curiosaDeckId: 'cmip2vwc100khl2043cgtpae7',
    avatar: 'Persecutor',
    avatarArtGroup: 'got-persecutor-pd',
    spellbook: [
      { name: 'Angry Mob', qty: 2 },
      { name: 'Backstab', qty: 2 },
      { name: 'Begone!', qty: 3 },
      { name: 'Betrayal', qty: 1 },
      { name: 'Blade of Thorns', qty: 1 },
      { name: 'Blaze', qty: 1 },
      { name: 'Cerberus in Chains', qty: 1 },
      { name: 'Clamor of Harpies', qty: 2 },
      { name: 'Clay Golem', qty: 3 },
      { name: 'Critical Strike', qty: 2 },
      { name: 'Detonate', qty: 3 },
      { name: 'Far East Assassin', qty: 1 },
      { name: 'Fey Changeling', qty: 2 },
      { name: 'Flagellant', qty: 3 },
      { name: 'Flaming Sword', qty: 1 },
      { name: 'Goswhit Helmet', qty: 1 },
      { name: 'Grief Larvae', qty: 3 },
      { name: 'Grievous Insult', qty: 2 },
      { name: 'Grigori Rasputin', qty: 1 },
      { name: 'Instigator Imp', qty: 3 },
      { name: 'Land Deed', qty: 1 },
      { name: 'Love Potion', qty: 1 },
      { name: 'Mad Dash', qty: 2 },
      { name: 'Panorama Manticore', qty: 1 },
      { name: 'Peacemaker Arbalest', qty: 1 },
      { name: 'Poison Nova', qty: 2 },
      { name: 'Poisonous Dagger', qty: 1 },
      { name: 'Profane Revenant', qty: 4 },
      { name: 'Sacred Scarabs', qty: 4 },
      { name: 'Screaming Skull', qty: 1 },
      { name: 'Sir Mordred', qty: 1 },
      { name: 'Sword and Shield', qty: 1 },
      { name: 'Symmetric Suffering', qty: 1 },
      { name: 'Torshammar Trinket', qty: 1 },
    ],
    atlas: [
      { name: 'Active Volcano', qty: 1 },
      { name: 'Algae Bloom', qty: 2 },
      { name: 'Arid Desert', qty: 2 },
      { name: 'Autumn River', qty: 2 },
      { name: 'Babbling Brook', qty: 3 },
      { name: 'Bog', qty: 2 },
      { name: 'Deep Sea', qty: 1 },
      { name: 'Desert Bloom', qty: 2 },
      { name: 'Forge', qty: 3 },
      { name: 'Hellmouth', qty: 1 },
      { name: 'Oasis', qty: 1 },
      { name: 'Red Desert', qty: 2 },
      { name: 'Remote Desert', qty: 2 },
      { name: 'Spring River', qty: 2 },
      { name: 'Summer River', qty: 2 },
      { name: 'Vast Desert', qty: 2 },
    ],
  },
  {
    id: 'harbinger',
    name: 'Harbinger',
    pool: 'gothic',
    elements: ['Air', 'Water'],
    description: 'Command an army of Spirits, Monsters, and strange horrors that strike from beyond the Void. Your Minions can appear where your opponent least expects them, turning careful positioning into sudden attacks and surprises. Use clever Magic and elusive threats to keep your enemy guessing as your army slips through the realm from one unexpected battle to the next.',
    strategy: 'Voidwalk Ambush',
    keyCards: ['Ultimate Horror', 'The Ninth Legion', 'Evil Twin'],
    tutorial: 'On setup, three random squares are marked as portal sites. Once per turn you can cast a minion to one of them for one mana less than normal. Snowball that discount turn after turn to land Yog-Sothoth and other eldritch horrors ahead of your opponent\'s curve.',
    curiosaDeckId: 'cmip2w8qa002mjl04intv339z',
    avatar: 'Harbinger',
    avatarArtGroup: 'got-harbinger-pd',
    spellbook: [
      { name: 'Aaj-kegon Ghost Crabs', qty: 1 },
      { name: 'Acid Rain', qty: 1 },
      { name: 'Arc Lightning', qty: 2 },
      { name: 'Dormant Monstrosity', qty: 1 },
      { name: 'Evil Twin', qty: 1 },
      { name: 'Forsaken', qty: 4 },
      { name: 'Gibbous Nightgaunts', qty: 3 },
      { name: 'Hauntless Head', qty: 3 },
      { name: 'Headless Haunt', qty: 3 },
      { name: 'Hyperparasite', qty: 1 },
      { name: 'Into the Abyss', qty: 1 },
      { name: 'Lacuna Entity', qty: 1 },
      { name: 'Lightning Bolt', qty: 2 },
      { name: 'Lucid Dreamers', qty: 1 },
      { name: 'Magic Missiles', qty: 2 },
      { name: 'Nommo Monitor', qty: 3 },
      { name: 'Peregrine Apparition', qty: 3 },
      { name: 'Phantom Steed', qty: 3 },
      { name: 'Phase Assassin', qty: 3 },
      { name: 'Scatter', qty: 2 },
      { name: 'Second Wind', qty: 2 },
      { name: 'Shoggoth', qty: 2 },
      { name: 'Spectral Stalker', qty: 4 },
      { name: 'Stargazer', qty: 1 },
      { name: 'Tactical Move', qty: 2 },
      { name: 'The Ninth Legion', qty: 1 },
      { name: 'Thundering Giant', qty: 1 },
      { name: 'Torshammar Trinket', qty: 1 },
      { name: 'Trial by Water', qty: 2 },
      { name: 'Ultimate Horror', qty: 1 },
      { name: 'Vril Revenant', qty: 1 },
      { name: 'Whirling Blades', qty: 1 },
    ],
    atlas: [
      { name: 'Accursed Tower', qty: 2 },
      { name: 'Algae Bloom', qty: 2 },
      { name: 'Autumn River', qty: 2 },
      { name: 'Beacon', qty: 1 },
      { name: 'Bog', qty: 2 },
      { name: 'Dark Tower', qty: 2 },
      { name: 'Deep Sea', qty: 1 },
      { name: 'Gothic Tower', qty: 2 },
      { name: 'Iceberg', qty: 3 },
      { name: 'Lighthouse', qty: 1 },
      { name: 'Lone Tower', qty: 2 },
      { name: 'Lookout', qty: 2 },
      { name: 'Mountain Peaks', qty: 1 },
      { name: 'Spring River', qty: 2 },
      { name: 'Summer River', qty: 2 },
      { name: 'The Manor at Daperyll Hill', qty: 1 },
      { name: 'Twilight Bloom', qty: 2 },
    ],
  },
];

// The full pool the picker shows at every milestone (L1 starter pick
// included). Already-owned decks render greyed out so each milestone
// effectively gives the player a free pick from whatever they don't
// own yet.
export const PROGRESSION_DECKS = [...STARTER_DECKS, ...GOTHIC_DECKS];

// Levels at which a player earns a new pre-con deck pick. Mirrors
// DECK_MILESTONE_LEVELS in valkenhall-server/src/utils/progression.js.
// the server is authoritative for pendingDeckGrants, this client copy
// is used purely to flavor the level-up celebration ("Level X! +Deck
// Pick") on the client without an extra round-trip. L1 isn't here:
// the starter pick is gated by `!arenaProfile.starterDeck` instead.
// DECK_GRANT_LEVELS and anyLevelInRangeGrantsDeck were retired with
// the Progression-screen rewrite. The canonical client-side constant
// now lives in utils/arena/profileDefaults.js (mirroring the server's
// utils/progression.js) and INCLUDES L1 as the first entry.
// [1, 2, 3, 5, 10, 15, 20, 25]. So the index into starter_decks_owned
// for "which deck was claimed at level N" lines up correctly.

// Names of every grantable pre-con deck. Used to tell a player's
// custom builds apart from the starter/progression decks the system
// saved on their behalf. The starter picker and milestone claim flows
// both write a saved-deck row using the pre-con's display name verbatim,
// so a savedDecks entry whose name is in this set is a granted pre-con
// (unless the player renamed it, which we treat as "personalised").
export const PROGRESSION_DECK_NAMES = new Set(PROGRESSION_DECKS.map((d) => d.name));

// The avatar card a progression deck is fronted by. Looked up by the deck's
// `avatar` name (identical to the deck name for all eight, but the field is
// what the grant reads, so displays follow the same source).
export function deckAvatarCard(deck, sorceryCards) {
  const name = deck?.avatar || deck?.name;
  if (!name || !Array.isArray(sorceryCards)) return null;
  return sorceryCards.find((c) => c.name === name && c.type === 'Avatar') || null;
}

// Art a progression deck's avatar is DISPLAYED with: the alt illustration the
// deck grants when it names one, else the booster art. Picks the group's
// standard printing. The synthetic foil carries the same image and the
// deck-picker surfaces render a plain <img>, no foil shader.
export function deckAvatarImageUrl(deck, sorceryCards) {
  const card = deckAvatarCard(deck, sorceryCards);
  if (!card) return null;
  const inGroup = printingsInArtGroup(card, deck?.avatarArtGroup || ART_GROUP_BASE);
  return (inGroup[0] || card.printings?.[0])?.image_url || null;
}

export function resolveStarterDeck(starterDeck, sorceryCards) {
  const nameIndex = new Map();
  for (const card of sorceryCards) {
    nameIndex.set(card.name.toLowerCase(), card);
  }

  const resolved = [];

  // Avatar. Prefer the foil printing when the card has one, so new
  // players get a sparkly avatar on their first deck save. Decks whose
  // real-world precon printed its avatar on a different illustration
  // (the four Gothic decks) name that art group, so the grant lands on
  // the alt art's own collection identity rather than the booster art.
  if (starterDeck.avatar) {
    const card = nameIndex.get(starterDeck.avatar.toLowerCase());
    if (card) {
      const artGroup = starterDeck.avatarArtGroup || ART_GROUP_BASE;
      const inGroup = printingsInArtGroup(card, artGroup);
      const printing = inGroup.find((p) => p.foiling === 'F') || inGroup[0] || card.printings?.[0];
      resolved.push({
        cardId: card.unique_id,
        printingId: printing?.unique_id || '',
        foiling: printing?.foiling || 'S',
        artGroup,
        zone: 'avatar',
      });
    }
  }

  // Spellbook + Atlas + Collection. Straightforward name lookups. Entries
  // whose names don't resolve are silently dropped so a missing card never
  // crashes the starter grant; the rest of the deck still imports cleanly.
  for (const zone of ['spellbook', 'atlas', 'collection']) {
    const entries = starterDeck[zone] || [];
    for (const entry of entries) {
      const card = nameIndex.get(entry.name.toLowerCase());
      if (!card) continue;
      const printing = card.printings?.[0];
      for (let i = 0; i < entry.qty; i++) {
        resolved.push({
          cardId: card.unique_id,
          printingId: printing?.unique_id || '',
          foiling: 'S',
          zone,
        });
      }
    }
  }

  return resolved;
}
